import React, { useState } from "react";
import styled from "styled-components";
import {
  FormControl,
  FormHiddenInput,
  FormInput,
  FormLabel,
} from "../FormControls";
import FormError from "../FormControls/FormError";
import { useTranslation } from "react-i18next";
import { Form, Formik } from "formik";
import {
  useSession,
  useUpdateProfileMutation,
} from "../../../queries/authentication.query";
import AlertMessage from "../AlertMessage";
import { editEmailSchema } from "../../../modules/Authorization/AuthorizationSchemas";
import ButtonOutlined from "../Buttons/ButtonOutlined";

const Wrapper = styled.div`
  width: 100%;

  @media (min-width: ${({ theme }) => theme.breakpoints.tablet}) {
    width: 640px;
  }
`;

const ErrorWrapper = styled.div`
  margin-bottom: 20px;
`;

const FormWrapper = styled.div`
  margin-bottom: 32px;
  display: grid;
  grid-row-gap: 32px;

  @media (min-width: ${({ theme }) => theme.breakpoints.tablet}) {
    grid-column-gap: 20px;
    flex-direction: column;
    grid-template-columns: 1fr 1fr;

    & > *:first-child {
      grid-column: span 2;
    }
  }
`;

const FormControlWrapper = styled.div``;

interface IEditEmailPayload {
  email: string;
  password: string;
  confirm_password: string;
}

interface IProps {
  className?: string;
  onSuccess?: (values: IEditEmailPayload) => void;
}

const EditEmailForm = ({ onSuccess, className }: IProps) => {
  const { t } = useTranslation();
  const { user: loggedUser } = useSession();
  const { mutateAsync: updateProfile } = useUpdateProfileMutation();
  const [error, setError] = useState("");

  const handleSubmit = async (values: IEditEmailPayload) => {
    try {
      await updateProfile({
        userId: loggedUser!.id,
        profileId: loggedUser!.profile_id,
        payload: {
          profile: {},
          email: values.email,
          password: values.password,
        },
      });
      onSuccess?.(values);
    } catch (error) {
      setError(t("signup-account-already-exists-error"));
    }
  };

  return (
    <Formik
      initialValues={{
        email: loggedUser?.email || "",
        password: "",
        confirm_password: "",
      }}
      validationSchema={editEmailSchema}
      onSubmit={handleSubmit}
    >
      {({ errors, touched, isSubmitting }) => (
        <Wrapper className={className}>
          {!!error && (
            <ErrorWrapper>
              <AlertMessage
                isOpen
                message={error}
                onClose={() => setError("")}
              />
            </ErrorWrapper>
          )}

          <Form noValidate>
            <FormWrapper>
              <FormControlWrapper>
                <FormControl>
                  <FormLabel htmlFor="email">{t("email")}</FormLabel>
                  <FormInput name="email" type="email" />
                  <FormError error={t(errors.email!)} touched={touched.email} />
                </FormControl>
              </FormControlWrapper>
              <FormControlWrapper>
                <FormControl>
                  <FormLabel htmlFor="password">{t("password")}</FormLabel>
                  <FormHiddenInput name="password" maxLength={20} />
                  <FormError
                    error={t(errors.password!)}
                    touched={touched.password}
                  />
                </FormControl>
              </FormControlWrapper>
              <FormControlWrapper>
                <FormControl>
                  <FormLabel htmlFor="confirm_password">
                    {t("confirm-password")}
                  </FormLabel>
                  <FormHiddenInput name="confirm_password" maxLength={20} />
                  <FormError
                    error={t(errors.confirm_password!)}
                    touched={touched.confirm_password}
                  />
                </FormControl>
              </FormControlWrapper>
            </FormWrapper>

            <ButtonOutlined
              fullWidth
              type="submit"
              text={t("save")}
              height="44px"
              isLoading={isSubmitting}
            />
          </Form>
        </Wrapper>
      )}
    </Formik>
  );
};

export default EditEmailForm;
